import React from 'react';

import PropTypes from 'prop-types';

import {Typography, Box, Stack} from 'views/components';
import {Translate, translate} from 'views/module';
import {Business} from 'views/icons';

import moment from 'moment';

const WorkHistorySection = (props) => {
  const {questions} = props;

  const workHistory = questions.workHistory || [];

  const yearsOfExperience = React.useMemo(() => {
    const totalMs = workHistory.reduce((total, {startDate, endDate}) => {
      const start = moment(startDate);
      const end = endDate ? moment(endDate) : moment();

      if (!start.isValid()) return total;
      if (!end.isValid() || end.isBefore(start)) return total;

      return total + end.diff(start);
    }, 0);

    return Math.round(moment.duration(totalMs).asYears());
  }, [workHistory]);


  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = moment(dateStr);
    if (!date.isValid()) return '';
    return date.format('MMM YYYY');
  };

  const getDuration = (start, end) => {
    if (!start) return '';

    const startDate = moment(start);
    const endDate = end ? moment(end) : moment();

    if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate)) return '';

    const duration = moment.duration(endDate.diff(startDate));
    const years = Math.floor(duration.asYears());
    const months = Math.floor(duration.asMonths() - years * 12);

    let result = '';
    if (years > 0) {
      if (months > 0) {
        result = translate('caregiverProfile.WorkHistory.Timeline.YearMonthFormat', {years, months});
      } else {
        result = translate('caregiverProfile.WorkHistory.Timeline.YearFormat', {years, months});
      }
    } else if (months > 0) {
      result = translate('caregiverProfile.WorkHistory.Timeline.MonthFormat', {years, months});
    }

    return result;
  };

  function workEntryHasMinimumData(workEntry) {
    return workEntry.employerName && workEntry.startDate;
  }

  return (
    workHistory.length > 0 && workHistory.some((e) => workEntryHasMinimumData(e)) &&
    <Stack alignItems="flex-start" width="100%" spacing={0.5}>
      <Typography variant="h5" fontWeight="bold">
        <Translate expression='caregiverProfile.WorkHistory.Title'/>
      </Typography>
      <Typography variant="subtitle1" sx={{color: 'grey.700', pb: 1}}>
        <Translate expression='caregiverProfile.WorkHistory.Subtitle'/>
      </Typography>
      {/* Work History Subsection */}
      <Stack position="relative" width="100%">
        <Box
          sx={{
            'position': 'absolute',
            'top': 0,
            'bottom': 0,
            'width': {xs: 36, sm: 44, md: 52, lg: 60}, // same as icon Box
            'pointerEvents': 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: 'divider',
              transform: 'translateX(-50%)',
            },
          }}
        />
        <Stack spacing={2} width="100%">
          {
            workHistory.map((e) => (
              workEntryHasMinimumData(e) &&
              <Stack
                key={e.employerName + e.startDate + e.endDate}
                direction="row"
                alignItems="flex-start"
                justifyContent="center"
                spacing={2}
                width="100%"
              >
                {/* Icon */}
                <Box
                  aria-hidden
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: {xs: 36, sm: 44, md: 52, lg: 60},
                    height: {xs: 36, sm: 44, md: 52, lg: 60},
                    aspectRatio: '1 / 1',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.75,
                  }}
                >
                  <Business sx={{fontSize: {xs: 20, sm: 24, md: 28, lg: 32}, color: 'text.secondary'}} />
                </Box>
                {/* Card */}
                <Stack
                  alignItems="flex-start" justifyContent="flex-start" spacing={0.5}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    bgcolor: 'white',
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    m: 0,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {e.employerName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="grey.700">
                      {formatDate(e.startDate)}
                      —
                      {(e.endDate ? formatDate(e.endDate) : translate('caregiverProfile.WorkHistory.EndDatePresent'))}
                    </Typography>
                    <Typography>
                      {'\u00B7'}
                    </Typography>
                    <Typography variant="body2" color="grey.700">
                      {getDuration(e.startDate, e.endDate)}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            ))
          }
        </Stack>
      </Stack>
      <Box sx={{height: 8}} />
      {/* Career Highlights Subsection */}
      {
        yearsOfExperience > 0 &&
        <Stack
          alignItems="flex-start"
          width="100%"
          spacing={0.5}
          sx={{
            bgcolor: '#F5F6FF',
            borderRadius: 3,
            px: 3,
            py: 3,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            <Translate expression='caregiverProfile.Highlights.Title'/>
          </Typography>
          <Stack direction="row" width="100%" justifyContent="space-around" spacing={1}>
            {/* Years of Experience */}
            <Stack alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                {`${String(yearsOfExperience)}+`}
              </Typography>
              <Typography variant="caption" sx={{color: 'grey.700'}}>
                <Translate expression='caregiverProfile.Highlights.YoE'/>
              </Typography>
            </Stack>
            {/* Client Satisfaction */}
            <Stack alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                {`100%`}
              </Typography>
              <Typography variant="caption" sx={{color: 'grey.700'}}>
                <Translate expression='caregiverProfile.Highlights.ClientSatisfaction'/>
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      }
    </Stack>
  );
};

WorkHistorySection.propTypes = {
  questions: PropTypes.object.isRequired,
};

export default WorkHistorySection;
